import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ChevronLeft } from "lucide-react";
import Seo from "@/components/site/Seo";

/**
 * Shared premium legal page shell.
 *
 * <LegalPage title="..." subtitle="..." eyebrow="..." seoDesc="...">
 *   <Section title="...">
 *     <p>...</p>
 *     <ul><li>...</li></ul>
 *   </Section>
 *   ...
 * </LegalPage>
 */
export default function LegalPage({
  title,
  eyebrow,
  subtitle,
  seoDesc,
  path,
  children,
}) {
  return (
    <div className="bg-[#0a0a0a] min-h-screen pb-24">
      <Seo
        title={`${title} · AutoRIO`}
        description={seoDesc || `${title} — AutoRIO Suceava`}
        path={path}
        noindex
      />

      {/* Hero */}
      <section className="relative overflow-hidden pt-32 pb-14 sm:pt-40 sm:pb-16">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(60% 55% at 50% 0%, rgba(212,175,55,0.12) 0%, transparent 60%)",
          }}
        />
        <div className="relative container-wide max-w-4xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-white/50 hover:text-[#D4AF37] transition-colors mb-6"
          >
            <ChevronLeft className="h-4 w-4" />
            Înapoi la pagina principală
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {eyebrow && <div className="eyebrow">{eyebrow}</div>}
            <h1 className="mt-3 font-display text-4xl sm:text-5xl md:text-[56px] font-extrabold text-white leading-[1.02] tracking-tight">
              {title}
            </h1>
            {subtitle && (
              <p className="mt-5 text-base sm:text-lg text-white/60 leading-relaxed max-w-2xl">
                {subtitle}
              </p>
            )}
            <div className="mt-6 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-white/40">
              <span className="h-1 w-6 bg-[#D4AF37]" />
              Ultima actualizare: {new Date().getFullYear()}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="container-wide max-w-4xl mx-auto">
        <div className="rounded-3xl border border-white/[0.06] bg-[#111111] p-6 sm:p-10 lg:p-14 shadow-[0_25px_70px_-25px_rgba(0,0,0,0.9)]">
          <article className="space-y-10 legal-prose">
            {children}
          </article>
        </div>

        {/* Bottom CTA */}
        <div className="mt-10 text-center">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.16em] text-white/60 hover:text-[#D4AF37] transition-colors"
          >
            Ai o întrebare? Contactează-ne
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}

export function LegalSection({ title, children }) {
  return (
    <section>
      <h2 className="font-display text-xl sm:text-2xl font-extrabold text-white tracking-tight leading-tight">
        <span className="inline-block h-2 w-2 bg-[#D4AF37] align-middle mr-3 rounded-full" />
        {title}
      </h2>
      <div className="mt-5 text-white/70 text-[15px] leading-[1.85] space-y-4">
        {children}
      </div>
    </section>
  );
}

export function LegalList({ items }) {
  return (
    <ul className="space-y-2.5 pl-1">
      {items.map((it, i) => (
        <li key={i} className="flex items-start gap-3 text-white/70">
          <span className="mt-2 h-1 w-3 shrink-0 bg-[#D4AF37]" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}
